import type { ZodSchema } from 'zod';

/**
 * Phase 2.7 — normalize a US/Canada phone-ish input to E.164 (`+1XXXXXXXXXX`).
 *
 * Rules:
 *   - Strip all non-digits.
 *   - 10 digits → prefix `+1`.
 *   - 11 digits starting with `1` → prefix `+`.
 *   - Anything else → return null (the form should reject + show an error).
 *
 * We don't try to normalize international numbers — Wildix dialplan expects
 * a 10-digit local NANP number for the `start` extension, so anything
 * non-NANP needs an admin to handle as a special case.
 */
export function normalizePhoneE164NANP(input: string): string | null {
  const digits = (input ?? '').replace(/\D+/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/**
 * Validate-only version that doesn't transform the input. Useful for
 * displaying a humane error message before normalize-on-blur.
 */
export function isValidPhoneE164NANP(input: string): boolean {
  return normalizePhoneE164NANP(input) !== null;
}


/**
 * Run a Zod schema and turn the issues into a {field: message} map keyed by
 * dot-joined path. UI components index into this map by field name to
 * surface per-field errors.
 */
export function fieldErrors(schema: ZodSchema, value: unknown): Record<string, string> | null {
  const result = schema.safeParse(value);
  if (result.success) return null;
  const out: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.') || '_root';
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}
